import { dateHeader } from "src/components/dataView/DataTable/tableHelpers";

/** Unit economics exposes revenue and costs per order, so their ratios match totals. */
const getProfitabilityMetrics = (row) => {
  const revenue = row?.aov || 0;
  const cogs = row?.cogs || 0;
  const grossProfit = row?.grossProfit || 0;
  const contribution = row?.profitContribution || 0;
  const netProfit = row?.netIncome || 0;
  const marketing = row?.directMarketing || 0;
  const productMargin = revenue ? (revenue - cogs) / revenue : 0;
  const grossProfitMargin = revenue ? grossProfit / revenue : 0;

  return {
    productMargin,
    grossProfitMargin,
    contributionMargin: revenue ? contribution / revenue : 0,
    netProfitMargin: revenue ? netProfit / revenue : 0,
    cogsPercent: revenue ? 1 - productMargin : 0,
    costOfDelivery: productMargin - grossProfitMargin,
    mer: marketing ? revenue / marketing : 0,
    // The service subtracts operating expenses from contribution to get net income.
    operatingExpenses: revenue ? (contribution - netProfit) / revenue : 0,
    profitContribution: contribution * (row?.orders || 0),
    netProfit: netProfit * (row?.orders || 0),
  };
};

const getProfitabilityKpi =
  (key, noTrend) => (unitEconomics, _, resolution) => {
    const allCustomers = (rows) =>
      (rows || []).filter((row) => row.returningCustomer === null);
    const rows = allCustomers(unitEconomics?.data);
    const summary = rows.find((row) => row.period === "Summary");
    const comparison = allCustomers(unitEconomics?.comparisonData).find(
      (row) => row.period === "Summary"
    );

    return {
      value: getProfitabilityMetrics(summary)[key],
      secondaryValue: getProfitabilityMetrics(comparison)[key],
      trend: noTrend
        ? null
        : rows
            .filter((row) => row.period !== "Summary")
            .sort((a, b) => a.period.localeCompare(b.period))
            .map((row) => ({
              period: dateHeader(row.period, resolution),
              value: getProfitabilityMetrics(row)[key],
            })),
    };
  };

export const getProfitabilityCards = (numberFormat) => {
  const percent = numberFormat.percentTimes100;
  const cards = {
    productMargin: { title: "Product Margin", format: percent },
    grossProfitMargin: { title: "Gross Profit Margin", format: percent },
    contributionMargin: { title: "Contribution Margin", format: percent },
    netProfitMargin: { title: "Net Profit Margin", format: percent },
    cogsPercent: { title: "COGS", format: percent, smallerIsGood: true },
    costOfDelivery: {
      title: "Cost of Delivery",
      format: percent,
      smallerIsGood: true,
    },
    mer: { title: "MER", format: (value) => `${numberFormat.normal(value)}x` },
    operatingExpenses: {
      title: "Operating Expenses",
      format: percent,
      smallerIsGood: true,
    },
    profitContribution: {
      title: "Profit Contribution",
      format: numberFormat.currency,
      noTrend: true,
    },
    netProfit: {
      title: "Net Profit",
      format: numberFormat.currency,
      noTrend: true,
    },
  };

  return Object.fromEntries(
    Object.entries(cards).map(([key, { noTrend = false, ...card }]) => [
      key,
      {
        ...card,
        chartClassName: "h-38",
        accessor: getProfitabilityKpi(key, noTrend),
      },
    ])
  );
};
